import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { getDisciplineName } from "@/domains/pm/utils/disciplineName";
import type { Discipline } from "@/domains/pm/types";

interface DisciplineMultiSelectListProps {
  disciplines: Discipline[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  emptyMessage: string;
  className?: string;
}

/**
 * WHA-3428 — checkbox discipline checklist. Shared between the PM issue
 * right-click context menu, the inline detail-panel editor and the New
 * Issue modal so the surfaces stay consistent.
 *
 * WHA-3449 — the list shows the full discipline name in the active
 * language (mirroring how /documents labels disciplines), not the gray
 * letter badge. The badge is reserved for the issue page itself.
 *
 * The checklist is sorted A–Z by the localized display name (the same
 * basis /documents sorts its discipline tag filter on). Sorting by the
 * displayed name — not the raw `name` column the store loads them in —
 * keeps it alphabetical in whichever language is active (English reads
 * `name_en`), and `localeCompare` with the active locale orders Estonian
 * diacritics (õ/ä/ö/ü) correctly.
 */
export function DisciplineMultiSelectList({
  disciplines,
  selectedIds,
  onChange,
  emptyMessage,
  className,
}: DisciplineMultiSelectListProps) {
  const { language } = useLanguage();

  const sortedDisciplines = useMemo(
    () =>
      [...disciplines].sort((a, b) =>
        getDisciplineName(a, language).localeCompare(getDisciplineName(b, language), language)
      ),
    [disciplines, language]
  );

  const toggle = (id: number) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  if (disciplines.length === 0) {
    return <p className="px-2 py-1.5 text-xs text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div className={cn("max-h-64 space-y-1 overflow-y-auto overscroll-contain", className)}>
      {sortedDisciplines.map((d) => (
        <label
          key={d.id}
          className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-muted"
        >
          <input
            type="checkbox"
            checked={selectedIds.includes(d.id)}
            onChange={() => toggle(d.id)}
            className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 accent-primary"
          />
          <span className="truncate">{getDisciplineName(d, language)}</span>
        </label>
      ))}
    </div>
  );
}
